import React from "react";
import { Link } from "react-router-dom";
import { Row, Col } from "reactstrap";

function Features() {
  return (
    <>
      <div className="content">
        {/* ── Hero Section ── */}
        <div className="forge-hero">
          <h1>FORGE YOUR <span>LIMITS.</span></h1>
          <p>
            Connect your Strava. Transform raw activity data into actionable
            performance insights. Speed percentiles. Sustained peaks. Recovery metrics.
            Data forged for athletes who demand more.
          </p>
          <div style={{ display: 'flex', gap: '16px', marginTop: '24px', position: 'relative', zIndex: 1 }}>
            <Link to="/admin/getactivities" className="forge-btn-primary btn" style={{ textDecoration: 'none' }}>
              <i className="fas fa-bolt" style={{ marginRight: '8px' }} />
              Get Started
            </Link>
            <a href="#features-detail" className="forge-btn-secondary btn" style={{ textDecoration: 'none' }}>
              Learn More
            </a>
          </div>
        </div>

        {/* ── Video Demo ── */}
        <div style={{ margin: '0 0 48px 0' }}>
          <div className="forge-subhead" style={{ marginBottom: '16px' }}>
            <i className="fas fa-play-circle" style={{ marginRight: '8px', color: 'var(--forge-primary)' }} />
            WATCH THE DEMO
          </div>
          <div style={{ 
            background: 'var(--forge-surface-low)', 
            border: '1px solid var(--forge-border)', 
            padding: '4px',
            clipPath: 'polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 16px 100%, 0 calc(100% - 16px))'
          }}>
            <iframe 
              width="100%" 
              height="450" 
              src="https://www.youtube.com/embed/jdpLdmoKcCo" 
              title="Irondrivestats.com" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
              style={{ display: 'block' }}
            />
          </div>
        </div>

        {/* ── Feature Cards ── */}
        <div id="features-detail">
          <div className="forge-subhead" style={{ marginBottom: '24px' }}>
            <i className="fas fa-crosshairs" style={{ marginRight: '8px', color: 'var(--forge-primary)' }} />
            CORE CAPABILITIES
          </div>
          <div className="forge-features">
            {/* Activity Feed */}
            <div className="forge-feature forge-animate-in">
              <div className="forge-feature-icon">
                <i className="fas fa-stream" />
              </div>
              <h3>Latest Activity Feed</h3>
              <p>
                View your most recent rides, runs, and activities with total distance 
                covered. All data pulled directly from your Strava account.
              </p>
            </div>

            {/* Speed Metrics */}
            <div className="forge-feature forge-animate-in" style={{ animationDelay: '100ms' }}>
              <div className="forge-feature-icon">
                <i className="fas fa-tachometer-alt" />
              </div>
              <h3>Speed Percentiles</h3>
              <p>
                <strong style={{ color: 'var(--forge-primary-bright)' }}>50th Percentile</strong> — Your median cruising speed, filtering out stops and extremes.
                <br/>
                <strong style={{ color: 'var(--forge-secondary)' }}>75th Percentile</strong> — Your power zone speed, highlighting your strongest performance segments.
              </p>
            </div>

            {/* Best Speeds */}
            <div className="forge-feature forge-animate-in" style={{ animationDelay: '200ms' }}>
              <div className="forge-feature-icon">
                <i className="fas fa-fire" />
              </div>
              <h3>Best Speeds Held</h3>
              <p>
                Track peak performance over 10, 20, and 30 minute intervals. 
                See how long you can sustain your fastest pace under pressure.
              </p>
            </div>

            {/* HRR */}
            <div className="forge-feature forge-animate-in" style={{ animationDelay: '300ms' }}>
              <div className="forge-feature-icon">
                <i className="fas fa-heartbeat" />
              </div>
              <h3>Iron Recovery Score</h3>
              <p>
                Heart rate recovery analysis — the minute with the biggest BPM drop 
                post-exercise. A clear measure of cardiovascular fitness and training efficiency.
              </p>
            </div>
          </div>
        </div>

        {/* ── Benefits Section ── */}
        <div style={{ marginTop: '64px' }}>
          <div className="forge-subhead" style={{ marginBottom: '24px' }}>
            <i className="fas fa-chart-line" style={{ marginRight: '8px', color: 'var(--forge-primary)' }} />
            WHY THESE METRICS MATTER
          </div>
          <Row>
            <Col md="6">
              <div className="forge-stat" style={{ marginBottom: '16px' }}>
                <div className="forge-stat-label">Insight</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--forge-text)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  High-Performance Segments
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--forge-text-muted)', marginTop: '8px' }}>
                  75th percentile speed gives you a focused view of your fastest sections, helping you track improvement.
                </div>
              </div>
              <div className="forge-stat" style={{ marginBottom: '16px' }}>
                <div className="forge-stat-label">Strategy</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--forge-text)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Pacing Analysis
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--forge-text-muted)', marginTop: '8px' }}>
                  Speed breakdown across percentiles reveals how well you pace yourself during rides and runs.
                </div>
              </div>
              <div className="forge-stat" style={{ marginBottom: '16px' }}>
                <div className="forge-stat-label">Training</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--forge-text)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Benchmarking & Progress
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--forge-text-muted)', marginTop: '8px' }}>
                  Median and quartile speeds help you set realistic performance benchmarks and track consistency.
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="forge-stat" style={{ marginBottom: '16px' }}>
                <div className="forge-stat-label">Endurance</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--forge-text)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Sustained Peak Performance
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--forge-text-muted)', marginTop: '8px' }}>
                  Best speeds over 10, 20, and 30 minutes reveal your endurance capabilities and strongest segments.
                </div>
              </div>
              <div className="forge-stat" style={{ marginBottom: '16px' }}>
                <div className="forge-stat-label">Recovery</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--forge-text)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Cardiovascular Fitness
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--forge-text-muted)', marginTop: '8px' }}>
                  Heart rate recovery metrics show how efficiently your body returns to baseline after intense efforts.
                </div>
              </div>
              <div className="forge-stat" style={{ marginBottom: '16px' }}>
                <div className="forge-stat-label">Competition</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 600, color: 'var(--forge-text)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                  Race Day Preparation
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--forge-text-muted)', marginTop: '8px' }}>
                  Analyze best speeds and recovery rates to fine-tune your strategy for competition.
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* ── CTA ── */}
        <div style={{ 
          textAlign: 'center', 
          padding: '64px 24px',
          background: 'linear-gradient(135deg, var(--forge-surface-mid) 0%, var(--forge-navy) 100%)',
          clipPath: 'polygon(0 16px, 100% 0, 100% calc(100% - 16px), 0 100%)',
          marginTop: '48px'
        }}>
          <div className="forge-headline" style={{ marginBottom: '8px' }}>
            Ready to forge your data?
          </div>
          <p style={{ color: 'var(--forge-text-muted)', marginBottom: '24px', fontFamily: 'var(--font-body)' }}>
            Connect your Strava account and unlock advanced performance insights.
          </p>
          <Link to="/admin/getactivities" className="forge-btn-primary btn" style={{ textDecoration: 'none' }}>
            <i className="fas fa-bolt" style={{ marginRight: '8px' }} />
            Connect & Analyze
          </Link>
        </div>

      </div>
    </>
  );
}

export default Features;
